import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { QmsUserSelect } from '../shared/QmsUserSelect';
import type {
  QmsQualityActionItem,
  QmsQualityActionStatus,
  QmsQualityActionType,
} from '../../types/qms.types';
import { extractId } from '../../types/qms.types';

interface QmsQualityActionItemsEditorProps {
  mode: 'capa' | 'change_control';
  items: QmsQualityActionItem[];
  onChange: (items: QmsQualityActionItem[]) => void;
  canVerify: boolean;
}

const LABELS: Record<QmsQualityActionType, string> = {
  immediate: 'Immediate corrections',
  corrective: 'Corrective actions',
  preventive: 'Preventive actions',
  implementation: 'Implementation tasks',
};

function newAction(type: QmsQualityActionType): QmsQualityActionItem {
  return {
    id: globalThis.crypto?.randomUUID?.() || `action-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type,
    description: '',
    owner_id: '',
    due_date: '',
    status: 'open',
    evidence: '',
  };
}

export function QmsQualityActionItemsEditor({
  mode,
  items,
  onChange,
  canVerify,
}: QmsQualityActionItemsEditorProps) {
  const groups: QmsQualityActionType[] = mode === 'capa'
    ? ['immediate', 'corrective', 'preventive']
    : ['implementation'];

  const updateItem = (id: string, patch: Partial<QmsQualityActionItem>) => {
    onChange(items.map((item) => item.id === id ? { ...item, ...patch } : item));
  };

  return (
    <div className="space-y-5">
      {groups.map((type) => {
        const groupItems = items.filter((item) => item.type === type);
        return (
          <section key={type} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-slate-900">{LABELS[type]}</h4>
                <p className="text-xs text-slate-500">Owner, hard due date, status, and objective evidence are audited.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onChange([...items, newAction(type)])}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add
              </Button>
            </div>

            {groupItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-sm text-slate-500">
                No {LABELS[type].toLowerCase()} recorded.
              </div>
            ) : (
              <div className="space-y-3">
                {groupItems.map((item, index) => {
                  const locked = item.status === 'verified';
                  return (
                    <div key={item.id} className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {LABELS[type].replace(/s$/, '')} {index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={locked}
                          onClick={() => onChange(items.filter((candidate) => candidate.id !== item.id))}
                          aria-label={`Remove ${LABELS[type].replace(/s$/, '')} ${index + 1}`}
                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`${item.id}-description`}>Action</Label>
                        <Textarea
                          id={`${item.id}-description`}
                          value={item.description}
                          disabled={locked}
                          onChange={(event) => updateItem(item.id, { description: event.target.value })}
                          rows={2}
                          placeholder="Describe the measurable action…"
                        />
                      </div>

                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="space-y-2 md:col-span-1">
                          <Label>Owner</Label>
                          <QmsUserSelect
                            value={item.owner_id}
                            disabled={locked}
                            onChange={(user) => updateItem(item.id, { owner_id: extractId(user) || '' })}
                            placeholder="Select owner"
                            ariaLabel={`${LABELS[type]} owner`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`${item.id}-due`}>Due date</Label>
                          <Input
                            id={`${item.id}-due`}
                            type="date"
                            disabled={locked}
                            value={item.due_date ? item.due_date.slice(0, 10) : ''}
                            onChange={(event) => updateItem(item.id, { due_date: event.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`${item.id}-status`}>Status</Label>
                          <select
                            id={`${item.id}-status`}
                            value={item.status}
                            disabled={locked}
                            onChange={(event) => updateItem(item.id, {
                              status: event.target.value as QmsQualityActionStatus,
                            })}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="open">Open</option>
                            <option value="complete">Complete</option>
                            <option value="verified" disabled={!canVerify}>Verified by QA</option>
                          </select>
                        </div>
                      </div>

                      {item.status !== 'open' && (
                        <div className="space-y-2">
                          <Label htmlFor={`${item.id}-evidence`}>Objective evidence</Label>
                          <Textarea
                            id={`${item.id}-evidence`}
                            value={item.evidence || ''}
                            disabled={locked}
                            onChange={(event) => updateItem(item.id, { evidence: event.target.value })}
                            rows={2}
                            placeholder="Identify the executed record, attachment, result, or verification evidence…"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
